import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Zap, CheckCircle, Clock, ArrowRight, ChevronRight, Home, Shield, Phone, Star, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/Button';
interface ExpressShippingPageProps {
  onNavigate?: (page: string) => void;
}
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12
    }
  }
};
export function ExpressShippingPage({
  onNavigate
}: ExpressShippingPageProps) {
  return <div className="flex flex-col bg-white" data-id="element-1886">
      {/* Hero Section */}
      <section className="relative min-h-[520px] lg:min-h-[600px] text-white overflow-hidden -mt-20 sm:-mt-[104px] pt-20 sm:pt-[104px]" data-id="element-1887">
        <div className="absolute inset-0" data-id="element-1888">
          <img src="https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80" alt="Cargo airplane" className="absolute inset-0 w-full h-full object-cover" data-id="element-1889" />
          <div className="absolute inset-0 bg-gray-900/65" data-id="element-1890" />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/80 via-gray-900/50 to-gray-900/30" data-id="element-1891" />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 via-transparent to-gray-900/30" data-id="element-1892" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-20 sm:pt-20 sm:pb-20 lg:pt-28 lg:pb-28" data-id="element-1893">
          <nav className="flex items-center gap-2 text-sm text-white/70 mb-8" data-id="element-1894">
            <button onClick={() => onNavigate?.('home')} className="flex items-center gap-1 hover:text-white transition-colors" data-id="element-1895">
              <Home className="w-4 h-4" data-id="element-1896" />
              <span data-id="element-1897">Home</span>
            </button>
            <ChevronRight className="w-4 h-4" data-id="element-1898" />
            <span className="text-white/50" data-id="element-1899">Services</span>
            <ChevronRight className="w-4 h-4" data-id="element-1900" />
            <span className="text-white font-medium" data-id="element-1901">Express Shipping</span>
          </nav>

          <motion.div initial="hidden" animate="visible" variants={fadeIn} className="max-w-3xl" data-id="element-1902">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-black/40 border border-white/30 text-white text-sm font-medium mb-6 backdrop-blur-sm" data-id="element-1903">
              <Zap className="w-4 h-4 text-yellow-400" data-id="element-1904" />
              Priority Service
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 leading-tight" style={{
            textShadow: '0 2px 4px rgba(0,0,0,0.5), 0 4px 12px rgba(0,0,0,0.4)'
          }} data-id="element-1905">
              Express Shipping —{' '}
              <span className="text-brand-300" data-id="element-1906">Delivered Fast</span>
            </h1>
            <p className="text-lg sm:text-xl text-white/90 mb-8 max-w-2xl leading-relaxed" style={{
            textShadow: '0 1px 3px rgba(0,0,0,0.5)'
          }} data-id="element-1907">
              When time is critical, our express service gets your package from
              the USA to Lagos in as little as 5–7 business days. Priority
              handling, priority customs, priority delivery.
            </p>
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-1908">
              <Button size="lg" className="bg-brand-500 hover:bg-brand-600 text-white border-none shadow-lg px-8" onClick={() => onNavigate?.('shipping')} data-id="element-1909">
                Ship Express Now <ArrowRight className="ml-2 w-5 h-5" data-id="element-1910" />
              </Button>
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white/10 px-8" onClick={() => onNavigate?.('contact')} data-id="element-1911">
                Get a Quote
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Express vs Standard */}
      <section className="py-20 lg:py-24 bg-white" data-id="element-1912">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1913">
          <div className="text-center mb-14" data-id="element-1914">
            <span className="inline-block px-4 py-1.5 bg-brand/10 text-brand text-sm font-semibold rounded-full mb-4" data-id="element-1915">
              Comparison
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" data-id="element-1916">
              Express vs Standard
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-1917">
              Choose the right speed for your needs.
            </p>
          </div>

          <div className="max-w-3xl mx-auto" data-id="element-1918">
            <div className="grid grid-cols-3 gap-0 rounded-2xl overflow-hidden border border-gray-200 shadow-sm" data-id="element-1919">
              {/* Header */}
              <div className="bg-gray-50 p-4 border-b border-gray-200" data-id="element-1920" />
              <div className="bg-gray-50 p-4 border-b border-gray-200 text-center" data-id="element-1921">
                <div className="text-sm font-semibold text-gray-500" data-id="element-1922">
                  Standard
                </div>
              </div>
              <div className="bg-brand-600 p-4 border-b border-brand-500 text-center" data-id="element-1923">
                <div className="text-sm font-bold text-white flex items-center justify-center gap-1" data-id="element-1924">
                  <Zap className="w-4 h-4 text-yellow-300" data-id="element-1925" /> Express
                </div>
              </div>

              {/* Rows */}
              <div className="p-4 border-b border-gray-100 text-sm font-medium text-gray-700 flex items-center" data-id="element-1926">
                Delivery Time
              </div>
              <div className="p-4 border-b border-gray-100 text-sm text-gray-500 text-center flex items-center justify-center" data-id="element-1927">
                7–14 days
              </div>
              <div className="p-4 border-b border-brand-500/20 bg-brand-50 text-sm font-semibold text-brand-700 text-center flex items-center justify-center" data-id="element-1928">
                5–7 days
              </div>

              <div className="p-4 border-b border-gray-100 text-sm font-medium text-gray-700 flex items-center" data-id="element-1929">
                Priority Handling
              </div>
              <div className="p-4 border-b border-gray-100 text-center flex items-center justify-center" data-id="element-1930">
                —
              </div>
              <div className="p-4 border-b border-brand-500/20 bg-brand-50 text-center flex items-center justify-center" data-id="element-1931">
                <CheckCircle className="w-5 h-5 text-brand-500" data-id="element-1932" />
              </div>

              <div className="p-4 border-b border-gray-100 text-sm font-medium text-gray-700 flex items-center" data-id="element-1933">
                Expedited Customs
              </div>
              <div className="p-4 border-b border-gray-100 text-center flex items-center justify-center" data-id="element-1934">
                —
              </div>
              <div className="p-4 border-b border-brand-500/20 bg-brand-50 text-center flex items-center justify-center" data-id="element-1935">
                <CheckCircle className="w-5 h-5 text-brand-500" data-id="element-1936" />
              </div>

              <div className="p-4 border-b border-gray-100 text-sm font-medium text-gray-700 flex items-center" data-id="element-1937">
                Real-time Tracking
              </div>
              <div className="p-4 border-b border-gray-100 text-center flex items-center justify-center" data-id="element-1938">
                <CheckCircle className="w-5 h-5 text-green-500" data-id="element-1939" />
              </div>
              <div className="p-4 border-b border-brand-500/20 bg-brand-50 text-center flex items-center justify-center" data-id="element-1940">
                <CheckCircle className="w-5 h-5 text-brand-500" data-id="element-1941" />
              </div>

              <div className="p-4 text-sm font-medium text-gray-700 flex items-center" data-id="element-1942">
                Dedicated Support
              </div>
              <div className="p-4 text-center flex items-center justify-center" data-id="element-1943">
                —
              </div>
              <div className="p-4 bg-brand-50 text-center flex items-center justify-center" data-id="element-1944">
                <CheckCircle className="w-5 h-5 text-brand-500" data-id="element-1945" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* When to use Express */}
      <section className="py-20 lg:py-24 bg-gray-50" data-id="element-1946">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1947">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-1948">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-1949">
              <span className="inline-block px-4 py-1.5 bg-brand/10 text-brand text-sm font-semibold rounded-full mb-6" data-id="element-1950">
                When to Choose Express
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6" data-id="element-1951">
                Time-Sensitive Shipments
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed" data-id="element-1952">
                Express shipping is ideal when every day counts. Whether it's a
                birthday gift, urgent business documents, or medical supplies —
                we get it there fast.
              </p>
              <div className="space-y-4" data-id="element-1953">
                <div className="flex items-start gap-3" data-id="element-1954">
                  <Star className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" data-id="element-1955" />
                  <div data-id="element-1956">
                    <div className="font-semibold text-gray-900" data-id="element-1957">
                      Gifts & Special Occasions
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-1958">
                      Birthdays, weddings, holidays — arrive on time, every
                      time.
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-1959">
                  <Star className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" data-id="element-1960" />
                  <div data-id="element-1961">
                    <div className="font-semibold text-gray-900" data-id="element-1962">
                      Business Documents
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-1963">
                      Contracts, legal papers, and critical business materials.
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-1964">
                  <Star className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" data-id="element-1965" />
                  <div data-id="element-1966">
                    <div className="font-semibold text-gray-900" data-id="element-1967">
                      Medical Supplies
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-1968">
                      Medications and health products that can't wait.
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-1969">
                  <Star className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" data-id="element-1970" />
                  <div data-id="element-1971">
                    <div className="font-semibold text-gray-900" data-id="element-1972">
                      Business Inventory
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-1973">
                      Restock quickly and keep your business running smoothly.
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} data-id="element-1974">
              <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm" data-id="element-1975">
                <div className="flex items-center gap-3 mb-6" data-id="element-1976">
                  <div className="w-12 h-12 bg-brand-50 rounded-xl flex items-center justify-center" data-id="element-1977">
                    <TrendingUp className="w-6 h-6 text-brand-600" data-id="element-1978" />
                  </div>
                  <div data-id="element-1979">
                    <div className="font-bold text-gray-900" data-id="element-1980">
                      Express Performance
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-1981">
                      Our track record speaks for itself
                    </div>
                  </div>
                </div>
                <div className="space-y-5" data-id="element-1982">
                  <div data-id="element-1983">
                    <div className="flex justify-between text-sm mb-2" data-id="element-1984">
                      <span className="text-gray-600" data-id="element-1985">On-time delivery</span>
                      <span className="font-semibold text-gray-900" data-id="element-1986">98.5%</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-1987">
                      <div className="h-full bg-brand-500 rounded-full" style={{
                      width: '98.5%'
                    }} data-id="element-1988" />
                    </div>
                  </div>
                  <div data-id="element-1989">
                    <div className="flex justify-between text-sm mb-2" data-id="element-1990">
                      <span className="text-gray-600" data-id="element-1991">
                        Customer satisfaction
                      </span>
                      <span className="font-semibold text-gray-900" data-id="element-1992">97%</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-1993">
                      <div className="h-full bg-brand-500 rounded-full" style={{
                      width: '97%'
                    }} data-id="element-1994" />
                    </div>
                  </div>
                  <div data-id="element-1995">
                    <div className="flex justify-between text-sm mb-2" data-id="element-1996">
                      <span className="text-gray-600" data-id="element-1997">Zero damage rate</span>
                      <span className="font-semibold text-gray-900" data-id="element-1998">99.9%</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-1999">
                      <div className="h-full bg-brand-500 rounded-full" style={{
                      width: '99.9%'
                    }} data-id="element-2000" />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-24 bg-white" data-id="element-2001">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2002">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-gradient-to-br from-brand-600 to-brand-700 rounded-3xl p-10 sm:p-16 shadow-xl" data-id="element-2003">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/20 text-white text-sm font-medium mb-6" data-id="element-2004">
              <Zap className="w-4 h-4 text-yellow-300" data-id="element-2005" /> Express Available Now
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" data-id="element-2006">
              Need it there fast?
            </h2>
            <p className="text-xl text-brand-100 mb-8 max-w-xl mx-auto" data-id="element-2007">
              Select express shipping when you create your shipment. 5–7 days,
              guaranteed.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2008">
              <Button variant="white" size="lg" className="px-10" onClick={() => onNavigate?.('shipping')} data-id="element-2009">
                Ship Express Now
              </Button>
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white/10 px-10" onClick={() => onNavigate?.('contact')} data-id="element-2010">
                Contact Us
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}